/* ────────────────────────────────────────────────────────────────────────────
 * packages/shared/src/api-schemas.ts
 *
 * zod 4 schemas for the request bodies the web app validates BEFORE it calls
 * the API. Spec: docs/spec/12-api-contract.md (every endpoint's request shape),
 *                §1.3 (money / quantity / factor), §1.4 (dates).
 *
 * ── DERIVATION DIRECTION — the rule this file exists to enforce ─────────────
 * The TYPES in `api-types.ts` are the contract; these schemas are DERIVED from
 * them, never the reverse. Nothing here uses `z.infer` as a source of truth.
 * Every schema is wrapped in `derivedFrom<T>()`, which is a compile-time gate:
 * the build fails if the schema's parsed output and the API type differ in
 * EITHER direction — a wrong field type, a missing required field, a field the
 * contract does not have, or an optional field made mandatory.
 *
 * ── WHAT THESE SCHEMAS ARE NOT ──────────────────────────────────────────────
 * They are a client-side convenience, not the security boundary. The API
 * re-validates every field with `class-validator` (12-api-contract.md §1.3
 * rule 5) and re-checks every permission. Never rely on a browser to reject a
 * bad body.
 *
 * ── MONEY ───────────────────────────────────────────────────────────────────
 * Money, quantity and factor are decimal STRINGS validated against the exact
 * regexes the API uses. They are NEVER `z.number()` and NEVER `z.coerce.*` —
 * coercion would float the string before any check ran, which is the same
 * failure mode as `enableImplicitConversion: true` on the server.
 * ──────────────────────────────────────────────────────────────────────────── */
import { z } from 'zod';
import { MONEY_PATTERN, QUANTITY_PATTERN, FACTOR_PATTERN, TIME_CATEGORIES, HOLIDAY_SCOPES, UNIT_CODE_MAX_LENGTH, UNIT_CODE_PATTERN, UNIT_NAME_MAX_LENGTH, UNIT_SHORT_LABEL_MAX_LENGTH, } from './api-types.js';
import { PERMISSIONS, ROLE_CODE_MAX_LENGTH, ROLE_CODE_PATTERN } from './permissions.js';
/* ═══════════════════════════ 0. THE DERIVATION GATE ═════════════════════════ */
/**
 * Bind a schema to the API type it must reproduce.
 *
 * `S extends z.ZodType<T>`  — the schema's output is a valid `T`.
 * the intersected conditional — `T` is fully covered by the schema's output.
 *
 * Together they are mutual assignability, so this catches drift in both
 * directions while still returning the CONCRETE schema type (`.shape`,
 * `.extend()`, `.partial()` all keep working on the exported value).
 */
function derivedFrom() {
    return (schema) => schema;
}
/* ═══════════════════════════ 1. PRIMITIVES ══════════════════════════════════ */
/** UUID v7 on the wire; the RFC 9562 layout passes zod's version 1–8 check. */
export const uuidSchema = z.uuid();
const ISO_DATE_PATTERN = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
const ISO_MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;
const ISO_DATE_TIME_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z$/;
/** `2026-02-31` matches the shape but is not a day. Reject it here rather than
 *  letting Postgres raise `22008` on the far side of a round-trip. */
const isRealCalendarDate = (s) => {
    const t = Date.parse(`${s}T00:00:00.000Z`);
    return !Number.isNaN(t) && new Date(t).toISOString().slice(0, 10) === s;
};
/** Business date, `YYYY-MM-DD`, interpreted in Asia/Kuala_Lumpur (§1.4). */
export const isoDateSchema = z
    .string()
    .regex(ISO_DATE_PATTERN, 'must be a calendar date, YYYY-MM-DD')
    .refine(isRealCalendarDate, 'is not a real calendar date');
/** Instant, ISO-8601 UTC with `Z` (§1.4). The API never emits a local offset. */
export const isoDateTimeSchema = z
    .string()
    .regex(ISO_DATE_TIME_PATTERN, 'must be an ISO-8601 UTC instant ending in Z');
/** `YYYY-MM` */
export const isoMonthSchema = z.string().regex(ISO_MONTH_PATTERN, 'must be YYYY-MM');
/** Money, `numeric(14,4)`. A STRING — `"144.0000"`, `"-1.5000"`. Never a number. */
export const moneySchema = z
    .string()
    .regex(MONEY_PATTERN, 'must be a decimal string with at most 4 decimal places');
/** Quantity, `numeric(14,3)`. */
export const quantitySchema = z
    .string()
    .regex(QUANTITY_PATTERN, 'must be a decimal string with at most 3 decimal places');
/** Factor (multiplier, percentage, weight), `numeric(9,6)`. */
export const factorSchema = z
    .string()
    .regex(FACTOR_PATTERN, 'must be a decimal string with at most 6 decimal places');
/** Casual time is captured in whole minutes, 0…1440 per row per category. */
export const minutesSchema = z.number().int().min(0).max(1440);
/** Optimistic concurrency token (§1.8). `0` for a NOT_STARTED day sheet. */
export const rowVersionSchema = z.number().int().min(0);
/** A non-empty trimmed string. `""` is never a legal value — the contract uses
 *  `null` for absent (`employeeId` is "null, never empty string", FR-2.1). */
const text = (max) => z.string().trim().min(1).max(max);
/** An optional free-text field: absent, or `null`, or a non-empty string. */
const optionalText = (max) => text(max).nullish();
/**
 * The LOGIN IDENTIFIER (FR-1.1), and `app_user.email` — `varchar(200)`, unique,
 * NOT NULL since migration 0014. zod 4 exposes the format check as a top-level
 * `z.email()`, not `z.string().email()`.
 *
 * Deliberately NOT lowercased here. The column stores lowercase and the API
 * normalises with `.trim().toLowerCase()` on the way in, so doing it a third
 * time in the browser would only hide a server that had stopped doing it.
 */
const emailField = z.email().max(200);
/* ═══════════════════════════ 2. ENUMS ═══════════════════════════════════════ */
/* Only the enums that appear in a request BODY are mirrored here.             */
/**
 * A role CODE is not an enum any more — an Admin creates roles at runtime, so
 * the only static rule is the format: UPPER_SNAKE, ≤ 32 chars (`varchar(32)`).
 * Whether the code exists is a database question the API answers.
 */
export const roleCodeSchema = z
    .string()
    .max(ROLE_CODE_MAX_LENGTH)
    .regex(ROLE_CODE_PATTERN, 'A role code is UPPER_SNAKE, e.g. RATE_REVIEWER.');
/**
 * A PERMISSION, by contrast, IS a closed enum: every endpoint declares a literal
 * string, so an invented permission would guard nothing. All 37, from the
 * runtime list that the compiler proves exhaustive against the union.
 */
export const permissionSchema = z.enum(PERMISSIONS);
export const shiftSchema = z.enum(['DAY', 'NIGHT']);
// ADR-0011. Same two words as `Shift`, deliberately a separate schema: a meal is not a shift.
export const mealTypeSchema = z.enum(['DAY', 'NIGHT']);
/**
 * A unit CODE is not an enum any more either (ADR-0017, migration 0051) — an Admin adds units
 * at Admin → Configuration → Units, so the only static rule is the format, the same move
 * `roleCodeSchema` above made. Whether the code exists, and is not retired, is a database
 * question the API answers (400 `E_VALIDATION` · 422 `E_UNIT_INACTIVE`).
 */
export const unitOfMeasureSchema = z
    .string()
    .max(UNIT_CODE_MAX_LENGTH)
    .regex(UNIT_CODE_PATTERN, 'A unit code is UPPER_SNAKE, e.g. METRIC_TONNE.');
export const statutoryCategorySchema = z.enum([
    'LOCAL_CITIZEN',
    'PERMANENT_RESIDENT',
    'FOREIGN_WORKER',
]);
export const workerStatusSchema = z.enum(['ACTIVE', 'SUSPENDED', 'TERMINATED']);
export const genderSchema = z.enum(['MALE', 'FEMALE', 'OTHER']);
export const paySchemeSchema = z.enum(['PIECE_RATE', 'CASUAL_TIME']);
export const rateKindSchema = z.enum(['PIECE', 'OVERRIDING']);
export const timeCategorySchema = z.enum(TIME_CATEGORIES);
export const otAllocationMethodSchema = z.enum(['EQUAL_SPLIT', 'HOURS_WEIGHTED']);
export const disbursementChannelSchema = z.enum(['CASH', 'BANK_IN']);
export const exportKindSchema = z.enum([
    'BANK_PAYMENT',
    'EPF',
    'SOCSO',
    'EIS',
    'PCB',
    'PAYROLL_REGISTER',
    'PAYSLIP_BUNDLE',
]);
export const fileFormatSchema = z.enum(['CSV', 'XLSX', 'PDF', 'JSON']);
/** `public_holiday.scope`. A zod enum over a `varchar + CHECK` column, the same
 *  arrangement as `exception_code.severity` — see `10-data-model.md` §5. */
export const holidayScopeSchema = z.enum(HOLIDAY_SCOPES);
/* ═══════════════════════════ 3. PAGINATION ══════════════════════════════════ */
/**
 * Offset pagination, on every list endpoint without exception (§1.5).
 * `z.coerce` is correct HERE and nowhere else: search params arrive as strings
 * and `page` is a count, not money.
 */
export const paginationQuerySchema = derivedFrom()(z.object({
    page: z.coerce.number().int().min(1).optional(),
    pageSize: z.coerce.number().int().min(1).max(200).optional(),
    sort: z.string().optional(),
}));
/* ═══════════════════════════ 4. AUTH (FR-1.1) ═══════════════════════════════ */
export const loginRequestSchema = derivedFrom()(z.object({
    email: emailField,
    password: z.string().min(1).max(256),
    rememberMe: z.boolean().optional(),
}));
/**
 * The 12-character floor is the client-side echo of the server's password
 * policy; the server owns it and answers `E_PASSWORD_POLICY` (§2.2).
 */
const passwordSchema = z.string().min(12).max(256);
export const changePasswordRequestSchema = derivedFrom()(z.object({
    currentPassword: z.string().min(1).max(256),
    newPassword: passwordSchema,
}));
export const forgotPasswordRequestSchema = derivedFrom()(z.object({ email: emailField }));
export const resetPasswordRequestSchema = derivedFrom()(z.object({ token: z.string().min(1), newPassword: passwordSchema }));
/* ═══════════════════════════ 4b. COMPANIES (tenancy) ════════════════════════ */
/**
 * The ONLY request body in the whole contract that names a company.
 *
 * Everywhere else the active company is the `X-Company-Id` header, validated
 * against the caller's memberships. That is deliberate: `forbidNonWhitelisted`
 * is on, so an un-DTO'd `companyId` in any other body is rejected with 400
 * rather than silently ignored — which means the API physically cannot be told
 * to act as a different company until somebody adds a field on purpose. This
 * endpoint is the exception because naming a company is its entire job.
 */
export const switchCompanyRequestSchema = derivedFrom()(z.object({ companyId: uuidSchema }));
/**
 * `.min(1)`: an account with no company can read nothing — every scoped endpoint
 * refuses it and the SPA strands it on `/no-company`. Emptying the set is
 * therefore never what an Admin meant; removing the last one is a separate,
 * deliberate act (and removing your OWN last one is refused outright).
 */
export const setUserCompaniesRequestSchema = derivedFrom()(z.object({ companyIds: z.array(uuidSchema).min(1) }));
/** UPPER_SNAKE, hyphens allowed — 'CFSB', 'CFSB-TG'. Immutable once created. */
const companyCodeSchema = z
    .string()
    .trim()
    .regex(/^[A-Z][A-Z0-9]*([-_][A-Z0-9]+)*$/)
    .max(32);
export const createCompanyRequestSchema = derivedFrom()(z.object({
    code: companyCodeSchema,
    name: text(200),
    shortName: text(60),
    stateCode: text(8).nullish(),
}));
export const updateCompanyRequestSchema = derivedFrom()(z.object({
    name: text(200).optional(),
    shortName: text(60).optional(),
    stateCode: text(8).nullish(),
    isActive: z.boolean().optional(),
}));
export const payslipLetterheadModeSchema = z.enum([
    'WITH',
    'WITHOUT',
]);
/* ═══════════════════════════ 5. USERS (§4.3) ════════════════════════════════ */
/** `email` is required — it is the login identifier, so there is no account
 *  without one. Every user holds at least one role (`.min(1)`). */
export const createUserRequestSchema = derivedFrom()(z.object({
    email: emailField,
    displayName: text(160),
    roleCodes: z.array(roleCodeSchema).min(1),
    temporaryPassword: passwordSchema.optional(),
}));
/** `.optional()`, not `.nullish()`: an email may be CHANGED, never cleared. */
export const updateUserRequestSchema = derivedFrom()(z.object({
    displayName: text(160).optional(),
    email: emailField.optional(),
    isActive: z.boolean().optional(),
}));
/* ═══════════════════════════ 5b. ROLES (§4.3, FR-1.3) ═══════════════════════ */
/** A landing route is an app path: it must start with `/`. */
const landingRouteSchema = z
    .string()
    .trim()
    .min(1)
    .max(64)
    .regex(/^\/[A-Za-z0-9/_-]*$/, 'A landing route is an app path, e.g. /rates.')
    .nullish();
export const createRoleRequestSchema = derivedFrom()(z.object({
    code: roleCodeSchema,
    name: text(80),
    description: optionalText(500),
    landingRoute: landingRouteSchema,
    permissions: z.array(permissionSchema),
}));
/** `code` is absent on purpose — a role code can never be changed (§4.3). */
export const updateRoleRequestSchema = derivedFrom()(z.object({
    name: text(80).optional(),
    description: optionalText(500),
    landingRoute: landingRouteSchema,
    sortOrder: z.number().int().min(0).max(10_000).optional(),
}));
export const setRolePermissionsRequestSchema = derivedFrom()(z.object({ permissions: z.array(permissionSchema) }));
/* ═══════════════════════════ 6. WORKFORCE (FR-2) ════════════════════════════ */
export const createWorkerRequestSchema = derivedFrom()(z.object({
    /** Nullable, unique-where-present. `null`, never `""` (FR-2.1). */
    employeeId: text(32).nullish(),
    name: text(160),
    nationality: optionalText(64),
    statutoryCategory: statutoryCategorySchema,
    agentId: uuidSchema.nullish(),
    payingEntityId: uuidSchema.nullish(),
    dateOfBirth: isoDateSchema.nullish(),
    gender: genderSchema.nullish(),
    joinedOn: isoDateSchema.nullish(),
    notes: optionalText(2000),
    /** ADR-0005. A string, like every other amount — `enableImplicitConversion: false` exists
     *  precisely so "500.0000" is never floated into 500. */
    depositAmount: moneySchema.nullish(),
    depositNote: optionalText(2000),
    /** ADR-0006. Unlike `depositAmount`, "0" is MEANINGFUL — it means this worker gets no
     *  medical allowance, which is different from null ("use the plant default"). */
    medicalAnnualEntitlement: moneySchema.nullish(),
}));
export const updateWorkerRequestSchema = derivedFrom()(createWorkerRequestSchema
    // The deposit is amended through its own endpoint, which can refuse once it has been
    // charged. See the note on `UpdateWorkerRequest`.
    .omit({ depositAmount: true, depositNote: true })
    .partial()
    .extend({
    status: workerStatusSchema.optional(),
    terminatedOn: isoDateSchema.nullish(),
}));
export const createWorkerDepositRequestSchema = derivedFrom()(z.object({
    amount: moneySchema,
    note: optionalText(2000),
}));
export const updateWorkerDepositRequestSchema = derivedFrom()(z.object({
    amount: moneySchema.optional(),
    note: optionalText(2000),
    cancel: z.boolean().optional(),
}));
export const releaseWorkerDepositRequestSchema = derivedFrom()(
// 20 characters is the same bar `payroll.reopen` sets, and `worker_deposit_release_chk`
// enforces it in the database too — a reason that survives the API but not the INSERT would
// be a 500 where a 400 belongs.
z.object({ reason: z.string().trim().min(20).max(2000) }));
export const createMedicalClaimRequestSchema = derivedFrom()(z.object({
    workerId: uuidSchema,
    claimDate: isoDateSchema,
    /** A string, like every other amount. `medical_claim_amount_chk` rejects <= 0 in the
     *  database; the API rejects it first so the caller gets a 400 rather than a mapped 409. */
    amount: moneySchema,
    description: optionalText(2000),
    receiptNo: optionalText(64),
    note: optionalText(2000),
}));
export const updateMedicalClaimRequestSchema = derivedFrom()(createMedicalClaimRequestSchema
    // Moving a claim between workers would move money between two annual balances in one
    // request. The correct act is to void it and record it again. See `UpdateMedicalClaimRequest`.
    .omit({ workerId: true })
    .partial());
export const voidMedicalClaimRequestSchema = derivedFrom()(
// Same 20-character bar as an early deposit release, and `medical_claim_void_chk` enforces it
// in the database too — a reason that survives the API but not the UPDATE would be a 500
// where a 400 belongs.
z.object({ reason: z.string().trim().min(20).max(2000) }));
export const setLeaveEntitlementRequestSchema = derivedFrom()(z.object({
    // A whole number of days: `worker_day_event` holds one row per worker per day, so there is
    // no half day to count. `worker_leave_entitlement_days_chk` holds the same 0–366 bound.
    days: z.number().int().min(0).max(366).nullable(),
    scope: z.enum(['ONWARD', 'YEAR_ONLY']).optional(),
    note: optionalText(500),
}));
export const setAttendanceEventRequestSchema = derivedFrom()(z.object({
    // NULLABLE and not optional: `null` is the value that CLEARS the event, so omitting the
    // field and sending null must not mean the same thing.
    exceptionCodeId: uuidSchema.nullable(),
    note: optionalText(500),
    // ADR-0007. Acting on the conflict warning is a second, deliberate request — never
    // inferred from the first. See `SetAttendanceEventRequest`.
    removeFromJobLines: z.boolean().optional(),
}));
export const confirmDerivedAbsencesRequestSchema = derivedFrom()(z.object({
    periodId: uuidSchema,
    // Omitted = confirm every unconfirmed derived cell in the period ("confirm all"); present
    // = just these. Not `.min(1)` — an empty array and an omitted field would otherwise mean
    // two different things by accident, and only the latter is the documented "confirm all".
    cells: z
        .array(z.object({ workerId: uuidSchema, workDate: isoDateSchema }))
        .optional(),
    // Not nullable, unlike `SetAttendanceEventRequest.exceptionCodeId` — there is no "clear"
    // gesture here, only "use the period's configured trigger code" (omitted) or "use this one".
    exceptionCodeId: uuidSchema.optional(),
}));
/* ── Canteen (ADR-0011) ─────────────────────────────────────────────────────── */
export const setCanteenMarkRequestSchema = derivedFrom()(z.object({
    // A boolean, not a nullable code: there is only "came" and "did not". `false` deletes.
    attended: z.boolean(),
    note: optionalText(500),
}));
export const bulkCanteenMarkRequestSchema = derivedFrom()(z.object({
    mealDate: isoDateSchema,
    // `.min(1)`: an empty list is a request to do nothing, and a 201 for nothing hides a
    // client bug — the "mark all granted" button must not send when nobody was granted.
    workerIds: z.array(uuidSchema).min(1),
    attended: z.boolean(),
}));
export const upsertBankAccountRequestSchema = derivedFrom()(z.object({
    bankCode: text(16),
    bankName: text(120),
    accountNo: text(34),
    accountHolder: text(160),
}));
export const createTeamRequestSchema = derivedFrom()(z.object({ code: text(16), name: text(120), description: optionalText(500) }));
export const updateTeamRequestSchema = derivedFrom()(createTeamRequestSchema.partial());
/** Half-open `[validFrom, validTo)`. `validTo` is EXCLUSIVE; `null` = open-ended.
 *  Clients send `validTo`, never the display-only `validToInclusive` (§1.4). */
export const createMembershipRequestSchema = derivedFrom()(z.object({
    workerId: uuidSchema,
    validFrom: isoDateSchema,
    validTo: isoDateSchema.nullish(),
    roleLabel: optionalText(120),
}));
export const updateMembershipRequestSchema = derivedFrom()(createMembershipRequestSchema.omit({ workerId: true }).partial());
/**
 * ADR-0013 — a leader window hangs off the TEAM, so there is no membership to check here and
 * none is implied: any worker may lead any team, and one worker may lead several at once. Half-
 * open like every other window (`validTo` EXCLUSIVE, `null`/absent = open-ended). Overlap is
 * adjudicated by `team_leader_no_overlap_excl` at INSERT — 409 `E_TEAM_LEADER_OVERLAP` — never
 * by a read-then-write check on either side of the wire.
 */
export const createTeamLeaderRequestSchema = derivedFrom()(z.object({
    workerId: uuidSchema,
    validFrom: isoDateSchema,
    validTo: isoDateSchema.nullish(),
    note: optionalText(500),
}));
/** End a live window. `validTo` is EXCLUSIVE — the day AFTER the leader's last day. */
export const endTeamLeaderRequestSchema = derivedFrom()(z.object({ validTo: isoDateSchema }));
/** W40 — the create shape minus the worker, every field optional (absent = unchanged). */
export const updateTeamLeaderRequestSchema = derivedFrom()(createTeamLeaderRequestSchema.omit({ workerId: true }).partial());
/**
 * ADR-0014 — a fixed RM amount moved each period off one worker's total overriding onto
 * another's. Shaped exactly like a leader window and for the same reasons: half-open,
 * `validTo` EXCLUSIVE and `nullish` so absent and `null` both mean open-ended, and the
 * (donor, recipient) overlap is adjudicated by `overriding_carve_out_no_overlap_excl` at
 * INSERT — 409 `E_OVERRIDING_CARVE_OUT_OVERLAP` — never by a read-then-write check.
 *
 * `amount` is `moneySchema` and so admits a negative here; the DB's `amount > 0` CHECK is what
 * refuses one. Kept unrefined on purpose, matching `createRateChangeRequestSchema`, so the
 * schema stays `.extend()`able; a form wanting to block submit should refine a copy.
 * `fromWorkerId !== toWorkerId` is likewise the database's CHECK, not a wire rule.
 */
export const createOverridingCarveOutRequestSchema = derivedFrom()(z.object({
    fromWorkerId: uuidSchema,
    toWorkerId: uuidSchema,
    amount: moneySchema,
    validFrom: isoDateSchema,
    validTo: isoDateSchema.nullish(),
    note: optionalText(500),
}));
/** End a live carve-out. `validTo` is EXCLUSIVE — the day AFTER its last period-end. */
export const endOverridingCarveOutRequestSchema = derivedFrom()(z.object({ validTo: isoDateSchema }));
/** ADR-0022 — correct a carve-out in place. The workers are fixed, as on a leader window (W40). */
export const updateOverridingCarveOutRequestSchema = derivedFrom()(createOverridingCarveOutRequestSchema.omit({ fromWorkerId: true, toWorkerId: true }).partial());
/**
 * ADR-0014 — a percentage of one worker's total overriding paid to another as extra money.
 *
 * `percent` is a `Factor` and is stored AS TYPED — `"10"` is 10%, not 0.1 — so it is bounded
 * to (0, 100] here as well as by the table's CHECK. `factorSchema` alone would accept `"-5"`
 * and `"999"`, and a percentage outside that range is a typo every time, so the refinement is
 * on the base schema rather than on a form-only copy: nothing legitimate wants to `.extend()`
 * past it. `Number()` is safe for the COMPARISON — the value stays a string on the wire and
 * the engine's arithmetic goes through decimal.js, never parseFloat.
 */
const overridingPercentSchema = factorSchema.refine((v) => Number(v) > 0 && Number(v) <= 100, 'must be a percentage greater than 0 and at most 100');
export const createOverridingPercentBonusRequestSchema = derivedFrom()(z.object({
    baseWorkerId: uuidSchema,
    toWorkerId: uuidSchema,
    percent: overridingPercentSchema,
    validFrom: isoDateSchema,
    validTo: isoDateSchema.nullish(),
    note: optionalText(500),
}));
/** End a live percentage bonus. `validTo` is EXCLUSIVE. */
export const endOverridingPercentBonusRequestSchema = derivedFrom()(z.object({ validTo: isoDateSchema }));
/** ADR-0022 — correct a percentage bonus in place. The workers are fixed. */
export const updateOverridingPercentBonusRequestSchema = derivedFrom()(createOverridingPercentBonusRequestSchema
    .omit({ baseWorkerId: true, toWorkerId: true })
    .partial());
export const confirmRosterRequestSchema = derivedFrom()(z.object({
    workDate: isoDateSchema,
    shift: shiftSchema.optional(),
    entries: z.array(z.object({
        workerId: uuidSchema,
        exceptionCodeId: uuidSchema.nullish(),
        note: optionalText(500),
    })),
}));
/* §5.3 — W26 / W27. The maxima are the API DTO's own `@MaxLength`s, field for
 * field; `contactEmail` is `optionalText`, not an email format, because
 * `CreateAgentRequestDto` validates it as a plain string (see the note on
 * `CreateAgentRequest`). */
export const createAgentRequestSchema = derivedFrom()(z.object({
    code: text(24),
    name: text(160),
    contactName: optionalText(120),
    contactPhone: optionalText(40),
    contactEmail: optionalText(160),
    address: optionalText(4000),
    notes: optionalText(4000),
}));
export const updateAgentRequestSchema = derivedFrom()(createAgentRequestSchema.partial());
/* ADR-0016 — J34 / J35 / J38 / J39, one pair for both lists. 160 is the column; the server
 * also collapses inner whitespace, which a schema cannot usefully restate. */
export const createJobMasterRequestSchema = derivedFrom()(z.object({
    name: text(160),
    note: optionalText(2000),
}));
export const updateJobMasterRequestSchema = derivedFrom()(z.object({
    name: text(160).optional(),
    note: optionalText(2000),
    isActive: z.boolean().optional(),
}));
export const createDocumentRequestSchema = derivedFrom()(z.object({
    documentTypeId: uuidSchema,
    documentNo: text(64),
    issuedOn: isoDateSchema.nullish(),
    expiresOn: isoDateSchema,
    notes: optionalText(2000),
}));
export const updateDocumentRequestSchema = derivedFrom()(createDocumentRequestSchema.partial());
/* ═══════════════════════════ 7. RATES (FR-3) ════════════════════════════════ */
/** Creating a work type never sets a rate — `initialRate` opens a PENDING
 *  change request instead (§6.1, and G2: no route writes `rate` directly). */
export const createWorkTypeRequestSchema = derivedFrom()(z.object({
    code: text(32),
    name: text(160),
    unit: unitOfMeasureSchema,
    description: optionalText(500),
    initialRate: z.object({ amount: moneySchema, validFrom: isoDateSchema }).nullish(),
}));
export const updateWorkTypeRequestSchema = derivedFrom()(createWorkTypeRequestSchema.pick({ name: true, description: true, unit: true }).partial());
/**
 * F38 `POST /config/units` (ADR-0017). `code` is immutable after this, so it is the one field
 * `updateUnitOfMeasureRequestSchema` leaves out — the `updateCasualSlotRequestSchema` move.
 */
export const createUnitOfMeasureRequestSchema = derivedFrom()(z.object({
    code: unitOfMeasureSchema,
    name: text(UNIT_NAME_MAX_LENGTH),
    shortLabel: text(UNIT_SHORT_LABEL_MAX_LENGTH),
    sortOrder: z.number().int().min(0).optional(),
}));
/** F38 `PATCH /config/units/{id}`. `isActive: false` retires, `true` reactivates. */
export const updateUnitOfMeasureRequestSchema = derivedFrom()(z.object({
    name: text(UNIT_NAME_MAX_LENGTH).optional(),
    shortLabel: text(UNIT_SHORT_LABEL_MAX_LENGTH).optional(),
    sortOrder: z.number().int().min(0).optional(),
    isActive: z.boolean().optional(),
}));
/**
 * `teamId` is REQUIRED for an OVERRIDING rate and FORBIDDEN for a PIECE rate
 * (FR-3.4: overriding rates vary by work type and team, never by worker). The
 * server re-checks; this refinement is what stops the form submitting at all.
 * A negative `proposedAmount` is legal — BR-3 penalty rates (FR-3.3).
 */
export const createRateChangeRequestSchema = derivedFrom()(z.object({
    kind: rateKindSchema,
    workTypeId: uuidSchema,
    teamId: uuidSchema.nullish(),
    proposedAmount: moneySchema,
    validFrom: isoDateSchema,
    reason: optionalText(1000),
}));
/** The same shape with the PIECE/OVERRIDING team rule applied. Use this one in
 *  the propose form; `createRateChangeRequestSchema` stays unrefined so it can
 *  still be `.extend()`ed. */
export const createRateChangeRequestFormSchema = createRateChangeRequestSchema.refine((v) => (v.kind === 'OVERRIDING' ? v.teamId != null : v.teamId == null), {
    path: ['teamId'],
    message: 'teamId is required for an OVERRIDING rate and must be omitted for a PIECE rate',
});
export const approveRateChangeRequestSchema = derivedFrom()(z.object({ note: optionalText(1000) }));
/* ═══════════════════════════ 8. JOB CAPTURE (FR-4) ══════════════════════════ */
/** A job line is addressed either by its uuid or, within one ops request, by
 *  `"@clientRef"` — an id the client minted for a line created earlier in the
 *  SAME batch (§7.4). So this is a non-empty string, not a uuid. */
const jobLineRefSchema = z.string().min(1).max(64);
export const daySheetOpSetHeaderSchema = derivedFrom()(z.object({
    op: z.literal('setHeader'),
    note: optionalText(2000),
}));
/** ADR-0023 — a sheet number is 1-based; the server answers `E_SHEET_NOT_FOUND` for one it lacks. */
const sheetNoSchema = z.number().int().min(1).max(99);
export const daySheetOpAddSheetSchema = derivedFrom()(z.object({ op: z.literal('addSheet'), jobPaperRef: optionalText(64), productionLine: optionalText(64) }));
export const daySheetOpSetSheetSchema = derivedFrom()(z.object({
    op: z.literal('setSheet'),
    sheetNo: sheetNoSchema,
    jobPaperRef: z.string().max(64).nullable().optional(),
    productionLine: z.string().max(64).nullable().optional(),
}));
export const daySheetOpRemoveSheetSchema = derivedFrom()(z.object({ op: z.literal('removeSheet'), sheetNo: sheetNoSchema }));
export const daySheetOpAddJobLineSchema = derivedFrom()(z.object({
    op: z.literal('addJobLine'),
    clientRef: z.string().min(1).max(64),
    workTypeId: uuidSchema,
    quantity: quantitySchema,
    jobOrderRef: optionalText(64),
    productId: uuidSchema.nullish(),
    customerId: uuidSchema.nullish(),
    /* Not `optionalText`: a blank name is meaningful (it clears), and the server trims. */
    productName: z.string().max(160).nullish(),
    customerName: z.string().max(160).nullish(),
    /** ADR-0023 — default 1. */
    sheetNo: sheetNoSchema.optional(),
    /** default true — the line above on the same sheet, else the roster, pre-selects the crew. */
    seedParticipantsFromRoster: z.boolean().optional(),
}));
export const daySheetOpUpdateJobLineSchema = derivedFrom()(z.object({
    op: z.literal('updateJobLine'),
    jobLineId: jobLineRefSchema,
    workTypeId: uuidSchema.optional(),
    quantity: quantitySchema.optional(),
    jobOrderRef: optionalText(64),
    productId: uuidSchema.nullish(),
    customerId: uuidSchema.nullish(),
    /* Not `optionalText`: a blank name is meaningful (it clears), and the server trims. */
    productName: z.string().max(160).nullish(),
    customerName: z.string().max(160).nullish(),
    note: optionalText(2000),
}));
export const daySheetOpRemoveJobLineSchema = derivedFrom()(z.object({ op: z.literal('removeJobLine'), jobLineId: jobLineRefSchema }));
export const daySheetOpReorderJobLinesSchema = derivedFrom()(z.object({
    op: z.literal('reorderJobLines'),
    jobLineIds: z.array(uuidSchema),
    sheetNos: z.array(sheetNoSchema).optional(),
}));
export const daySheetOpSetParticipantsSchema = derivedFrom()(z.object({
    op: z.literal('setParticipants'),
    jobLineId: jobLineRefSchema,
    workerIds: z.array(uuidSchema),
}));
export const daySheetOpAddParticipantSchema = derivedFrom()(z.object({
    op: z.literal('addParticipant'),
    jobLineId: jobLineRefSchema,
    workerId: uuidSchema,
}));
export const daySheetOpRemoveParticipantSchema = derivedFrom()(z.object({
    op: z.literal('removeParticipant'),
    jobLineId: jobLineRefSchema,
    workerId: uuidSchema,
}));
/** `overridingRate: null` REMOVES the overriding from the line — rate and amount both become
 *  null (FR-4.6). Who is credited comes from the team's `team_leader` window (ADR-0013), so
 *  there is no leader on the line for this op to keep or clear. */
export const daySheetOpSetOverridingBonusSchema = derivedFrom()(z.object({
    op: z.literal('setOverridingBonus'),
    jobLineId: jobLineRefSchema,
    overridingRate: moneySchema.nullable(),
}));
export const daySheetOpAddCrewMemberSchema = derivedFrom()(z.object({
    op: z.literal('addCrewMember'),
    workerId: uuidSchema,
    isGuest: z.boolean().optional(),
}));
export const daySheetOpRemoveCrewMemberSchema = derivedFrom()(z.object({ op: z.literal('removeCrewMember'), workerId: uuidSchema }));
/** `exceptionCodeId: null` clears the exception. Whether the worker then leaves
 *  the divisor is configuration (OQ-8), never a client decision. */
export const daySheetOpSetExceptionSchema = derivedFrom()(z.object({
    op: z.literal('setException'),
    workerId: uuidSchema,
    exceptionCodeId: uuidSchema.nullable(),
    note: optionalText(500),
}));
/** BR-7: the OT pot is an amount in Ringgit per team per day, not hours. The
 *  hours column is a record, not a driver (README §4 conflict #16). */
export const daySheetOpSetOtPotSchema = derivedFrom()(z.object({
    op: z.literal('setOtPot'),
    potAmount: moneySchema,
    allocationMethod: otAllocationMethodSchema.optional(),
    note: optionalText(500),
}));
export const daySheetOpSetOtParticipationSchema = derivedFrom()(z.object({
    op: z.literal('setOtParticipation'),
    workerId: uuidSchema,
    included: z.boolean(),
    otHours: quantitySchema.nullish(),
}));
export const daySheetOpSetMealGrantSchema = derivedFrom()(z.object({
    op: z.literal('setMealGrant'),
    workerId: uuidSchema,
    // ADR-0011 — which tick column. Required, so an old client that omits it is refused
    // rather than silently granting a day meal (or a night one) nobody chose.
    mealType: mealTypeSchema,
    granted: z.boolean(),
    note: optionalText(500),
}));
/** All 18 operations of §7.4, discriminated on `op`. */
export const daySheetOpSchema = derivedFrom()(z.discriminatedUnion('op', [
    daySheetOpSetHeaderSchema,
    daySheetOpAddSheetSchema,
    daySheetOpSetSheetSchema,
    daySheetOpRemoveSheetSchema,
    daySheetOpAddJobLineSchema,
    daySheetOpUpdateJobLineSchema,
    daySheetOpRemoveJobLineSchema,
    daySheetOpReorderJobLinesSchema,
    daySheetOpSetParticipantsSchema,
    daySheetOpAddParticipantSchema,
    daySheetOpRemoveParticipantSchema,
    daySheetOpSetOverridingBonusSchema,
    daySheetOpAddCrewMemberSchema,
    daySheetOpRemoveCrewMemberSchema,
    daySheetOpSetExceptionSchema,
    daySheetOpSetOtPotSchema,
    daySheetOpSetOtParticipationSchema,
    daySheetOpSetMealGrantSchema,
]));
/** 1…100 ops, applied in order, all-or-nothing (§7.4). The `reason` that used to
 *  ride here for a post-close edit went with the per-day lifecycle (ADR-0012). */
export const daySheetOpsRequestSchema = derivedFrom()(z.object({
    expectedRowVersion: rowVersionSchema,
    ops: z.array(daySheetOpSchema).min(1).max(100),
    clientTimestamp: isoDateTimeSchema.optional(),
}));
/* ═══════════════════════════ 9. CASUAL TIME (FR-5) ══════════════════════════ */
/**
 * F20 `POST /config/casual-slots` — the casual pay master an engagement points
 * at. It lives in this section, not with the other `/config/*` bodies, because
 * the row it writes is the fallback half of the FR-5.1 rate-resolution chain
 * (`engagement.dailyRate ?? casualSlot.defaultDailyRate`) that
 * `createEngagementRequestSchema` below completes.
 *
 * `defaultHoursPerDay` uses `factorSchema` (6 dp), NOT `quantitySchema` (3 dp).
 * That is deliberate and matches the API's `@Matches(FACTOR_RE)` on
 * `UpsertCasualSlotDto`: the column is `numeric(9,6)`, so validating at 3 dp
 * here would reject values Postgres accepts. The RESPONSE renders the same
 * column at `Quantity` scale for parity with `EngagementDto.hoursPerDay` — the
 * asymmetry is recorded on `CasualSlotDto.defaultHoursPerDay`.
 *
 * There is no `hourlyRate` field: R-5 derives it server-side and a client that
 * could send one would be a second implementation of the rule.
 */
export const upsertCasualSlotRequestSchema = derivedFrom()(z.object({
    /** `casual_slot.code` is `varchar(24)` and unique — "GEN-D", "FORK-D". */
    code: text(24),
    name: text(160),
    roleTitle: optionalText(120),
    shift: shiftSchema.optional(),
    defaultDailyRate: moneySchema,
    defaultHoursPerDay: factorSchema,
}));
/** `code` is omitted: engagements reference the slot, so the code is immutable
 *  once written and `PatchCasualSlotDto` on the API refuses it likewise. */
export const updateCasualSlotRequestSchema = derivedFrom()(upsertCasualSlotRequestSchema.omit({ code: true }).partial());
export const createEngagementRequestSchema = derivedFrom()(z.object({
    workerId: uuidSchema,
    payScheme: paySchemeSchema,
    casualSlotId: uuidSchema.nullish(),
    dailyRate: moneySchema.nullish(),
    /** Defaults from `payroll_policy.casual_hours_per_day` when omitted. */
    hoursPerDay: quantitySchema.nullish(),
    shift: shiftSchema.nullish(),
    validFrom: isoDateSchema,
    validTo: isoDateSchema.nullish(),
    note: optionalText(1000),
}));
export const updateEngagementRequestSchema = derivedFrom()(createEngagementRequestSchema.omit({ workerId: true }).partial());
/**
 * WHOLE-DAY REPLACE (§8.2). The client sends every category it wants to exist;
 * a category omitted, or sent as 0, deletes its row. Sending one changed cell
 * would silently keep a stale sibling.
 */
export const upsertCasualDayRequestSchema = derivedFrom()(z.object({
    workerId: uuidSchema,
    workDate: isoDateSchema,
    entries: z.array(z.object({ category: timeCategorySchema, minutes: minutesSchema })),
    note: optionalText(1000),
}));
/**
 * C9 — the batch is scoped to ONE pay period and versioned against it. A day
 * outside the period is refused by the server (`dayOutsidePeriod`); the client
 * cannot check that here because the schema does not know the period's dates.
 * `max(31)` is the longest calendar month, which is the longest a period can be.
 */
export const casualOpsRequestSchema = derivedFrom()(z.object({
    workerId: uuidSchema,
    periodId: uuidSchema,
    expectedRowVersion: rowVersionSchema,
    days: z.array(upsertCasualDayRequestSchema).min(1).max(31),
}));
/** C13 (ADR-0024) — `null` clears the day's meal. */
export const setCasualMealRequestSchema = derivedFrom()(z.object({
    workerId: uuidSchema,
    workDate: isoDateSchema,
    mealType: mealTypeSchema.nullable(),
}));
/* ═══════════════════════════ 10. PAYROLL (FR-6) ═════════════════════════════ */
/** `endDate` is INCLUSIVE — a pay period is a closed interval, unlike every
 *  effective-dated window in the system (§13 `PayPeriodDto`). */
export const createPayPeriodRequestSchema = derivedFrom()(z.object({
    label: text(64),
    startDate: isoDateSchema,
    endDate: isoDateSchema,
    payDate: isoDateSchema.nullish(),
}));
export const createPayPeriodRequestFormSchema = createPayPeriodRequestSchema.refine((v) => v.startDate <= v.endDate, { path: ['endDate'], message: 'endDate must not be before startDate' });
/**
 * FR-6.1 — the monthly cut-off, expressed as the day a human says.
 *
 * 27 is the ceiling because the stored `startDay` is `cutoffDay + 1`, and 28 is
 * the highest day of the month that February is guaranteed to have. A cut-off of
 * the 30th cannot be honoured every month, and clamping it would silently reshape
 * that month's payroll.
 */
export const updatePeriodBoundaryRequestSchema = derivedFrom()(z.object({
    cutoffDay: z.number().int().min(1).max(27).nullable(),
    autoRoll: z.boolean().optional(),
    validFrom: isoDateSchema.optional(),
}));
export const executeRunRequestSchema = derivedFrom()(z.object({ force: z.boolean().optional(), note: optionalText(1000) }));
export const lockRunRequestSchema = derivedFrom()(z.object({
    note: optionalText(1000),
    reconciliationBaselineId: uuidSchema.nullish(),
}));
export const createPayComponentRequestSchema = derivedFrom()(z.object({
    periodId: uuidSchema,
    workerId: uuidSchema,
    payComponentTypeId: uuidSchema,
    /** Signed. A DEDUCTION type carries the sign its configuration defines. */
    amount: moneySchema,
    quantity: quantitySchema.nullish(),
    effectiveDate: isoDateSchema.optional(),
    note: optionalText(1000),
}));
export const updatePayComponentRequestSchema = derivedFrom()(createPayComponentRequestSchema
    .pick({ amount: true, quantity: true, effectiveDate: true, note: true })
    .partial());
/** P32 — blank is allowed on the wire: it is how a remark is cleared. */
export const putPayrollRemarkRequestSchema = derivedFrom()(z.object({ remark: z.string().max(2000) }));
/** BR-13: exactly ONE disbursement channel per payroll line. */
export const setDisbursementRequestSchema = derivedFrom()(z.object({
    channel: disbursementChannelSchema,
    payingEntityId: uuidSchema.nullish(),
    note: optionalText(500),
}));
export const createExportRequestSchema = derivedFrom()(z.object({
    kind: exportKindSchema,
    format: fileFormatSchema.optional(),
    payingEntityId: uuidSchema.nullish(),
    /** BANK_PAYMENT only. */
    channel: disbursementChannelSchema.optional(),
    workerIds: z.array(uuidSchema).optional(),
    /** PAYSLIP_BUNDLE only. Omitted ⇒ the `app_config` default. */
    letterhead: payslipLetterheadModeSchema.optional(),
}));
/* ═════════════════════ 10b. PUBLIC HOLIDAYS (F24a–F24d) ═════════════════════ */
/**
 * A holiday is a POINT, not a window — there is no `validFrom`/`validTo` here, and
 * no overlap guard. Uniqueness is `(date, scope)` among live rows, enforced by the
 * partial index `public_holiday_date_scope_uq` (migration 0015).
 */
export const createPublicHolidayRequestSchema = derivedFrom()(z.object({
    date: isoDateSchema,
    name: text(120),
    scope: holidayScopeSchema.optional(),
    note: optionalText(500),
}));
export const updatePublicHolidayRequestSchema = derivedFrom()(createPublicHolidayRequestSchema.partial());
/**
 * The pasted gazette, and the "copy last year forward" plan. `dryRun` is what the
 * preview table runs on: the server reports CREATED / DUPLICATE / INVALID per row
 * and writes nothing, so the clerk sees the outcome before committing to it.
 *
 * The 400 cap is a year of holidays several times over — it exists so a runaway
 * paste cannot open a transaction the size of the table.
 */
export const importPublicHolidaysRequestSchema = derivedFrom()(z.object({
    holidays: z.array(createPublicHolidayRequestSchema).min(1).max(400),
    dryRun: z.boolean().optional(),
}));
/* ═══════════════════════════ 11. INDEX ══════════════════════════════════════ */
/**
 * Every request-body schema, keyed by the API type it derives from. Useful for
 * a generic form component and for the test that asserts none was forgotten.
 */
export const REQUEST_SCHEMAS = {
    LoginRequest: loginRequestSchema,
    ChangePasswordRequest: changePasswordRequestSchema,
    ForgotPasswordRequest: forgotPasswordRequestSchema,
    ResetPasswordRequest: resetPasswordRequestSchema,
    CreateUserRequest: createUserRequestSchema,
    UpdateUserRequest: updateUserRequestSchema,
    CreateWorkerRequest: createWorkerRequestSchema,
    UpdateWorkerRequest: updateWorkerRequestSchema,
    UpsertBankAccountRequest: upsertBankAccountRequestSchema,
    CreateTeamRequest: createTeamRequestSchema,
    UpdateTeamRequest: updateTeamRequestSchema,
    CreateMembershipRequest: createMembershipRequestSchema,
    UpdateMembershipRequest: updateMembershipRequestSchema,
    CreateTeamLeaderRequest: createTeamLeaderRequestSchema,
    EndTeamLeaderRequest: endTeamLeaderRequestSchema,
    UpdateTeamLeaderRequest: updateTeamLeaderRequestSchema,
    CreateOverridingCarveOutRequest: createOverridingCarveOutRequestSchema,
    EndOverridingCarveOutRequest: endOverridingCarveOutRequestSchema,
    CreateOverridingPercentBonusRequest: createOverridingPercentBonusRequestSchema,
    EndOverridingPercentBonusRequest: endOverridingPercentBonusRequestSchema,
    ConfirmRosterRequest: confirmRosterRequestSchema,
    ConfirmDerivedAbsencesRequest: confirmDerivedAbsencesRequestSchema,
    CreateAgentRequest: createAgentRequestSchema,
    UpdateAgentRequest: updateAgentRequestSchema,
    CreateJobMasterRequest: createJobMasterRequestSchema,
    UpdateJobMasterRequest: updateJobMasterRequestSchema,
    CreateDocumentRequest: createDocumentRequestSchema,
    UpdateDocumentRequest: updateDocumentRequestSchema,
    CreateWorkTypeRequest: createWorkTypeRequestSchema,
    UpdateWorkTypeRequest: updateWorkTypeRequestSchema,
    CreateUnitOfMeasureRequest: createUnitOfMeasureRequestSchema,
    UpdateUnitOfMeasureRequest: updateUnitOfMeasureRequestSchema,
    CreateRateChangeRequest: createRateChangeRequestSchema,
    ApproveRateChangeRequest: approveRateChangeRequestSchema,
    DaySheetOpsRequest: daySheetOpsRequestSchema,
    UpsertCasualSlotRequest: upsertCasualSlotRequestSchema,
    UpdateCasualSlotRequest: updateCasualSlotRequestSchema,
    CreateEngagementRequest: createEngagementRequestSchema,
    UpdatePeriodBoundaryRequest: updatePeriodBoundaryRequestSchema,
    UpdateEngagementRequest: updateEngagementRequestSchema,
    UpsertCasualDayRequest: upsertCasualDayRequestSchema,
    SetCasualMealRequest: setCasualMealRequestSchema,
    CasualOpsRequest: casualOpsRequestSchema,
    CreatePayPeriodRequest: createPayPeriodRequestSchema,
    ExecuteRunRequest: executeRunRequestSchema,
    LockRunRequest: lockRunRequestSchema,
    CreatePayComponentRequest: createPayComponentRequestSchema,
    UpdatePayComponentRequest: updatePayComponentRequestSchema,
    PutPayrollRemarkRequest: putPayrollRemarkRequestSchema,
    SetDisbursementRequest: setDisbursementRequestSchema,
    CreateExportRequest: createExportRequestSchema,
    CreatePublicHolidayRequest: createPublicHolidayRequestSchema,
    UpdatePublicHolidayRequest: updatePublicHolidayRequestSchema,
    ImportPublicHolidaysRequest: importPublicHolidaysRequestSchema,
};
//# sourceMappingURL=api-schemas.js.map